import { formSubmitWithWaitForJobCompletion } from "../../../../scripts/jobCompletion";
import * as Turbo from "@hotwired/turbo";
import { initializeTooltips } from "../../../../scripts/tooltips";
import { translate } from "../../../../scripts/translate";
import { getScoreFunctionPreservingOrderInSearch, selectize } from "../../../../scripts/selectizeHelper";
import "./Translation.scss";
import { TranslationMode } from "../../../../scripts/_generated/TranslationMode";
import { TranslationStimulusModel } from "../../../../scripts/_generated/TranslationStimulusModel";
import BaseStimulusModelController from "../../../BaseStimulusModelController";
import { dialogCancelButtonClassName, dialogConfirmationButtonClassName } from "../../../../scripts/dialogConstants";
import * as TranslationsKeys from "../../../../scripts/locale/translationsKeys";

export default class TranslationController extends BaseStimulusModelController<TranslationStimulusModel> {
    static targets = [
        "form",
        "submit",
        "selectedDeepLTranslationSourceLanguage",
        "selectedDeepLTranslationTargetLanguage",
        "selectedManualTranslationTargetLanguage",
        "translateDeepLBox",
        "translateManuallyBox",
        "translationMode",
        
        "translateDestinationOverview",
        "translateDestinationOverviewOptions",
        
        "deepLTranslateDestinationPois",
        "deepLTranslateDestinationPoisOptions",
        "overwriteExistingDestinationPoiTranslation",
        "overwriteExistingDestinationPoiTranslationOptions",

        "deepLTranslateTourAndTourPois",
        "deepLTranslateTourAndTourPoisOptions",
        "translateTourOverview",
        "translateTourOverviewOptions",

        "translateTourPois",
        "translateTourPoisOptions",
        "overwriteExistingTourPoiTranslation",
        "overwriteExistingTourPoiTranslationOptions",

        "includePrivateContent",

        "disableSafetyChecks"
    ];
       
    readonly formTarget!: HTMLElement;
    readonly submitTarget!: HTMLElement;
    readonly selectedDeepLTranslationSourceLanguageTarget!: HTMLSelectElement;
    readonly selectedDeepLTranslationTargetLanguageTarget!: HTMLSelectElement;
    readonly selectedManualTranslationTargetLanguageTarget!: HTMLSelectElement;
    readonly translateDeepLBoxTarget!: HTMLDivElement;
    readonly translateManuallyBoxTarget!: HTMLDivElement;
    readonly translationModeTarget!: HTMLInputElement;
    
    readonly translateDestinationOverviewTarget!: HTMLInputElement;
    readonly translateDestinationOverviewOptionsTarget!: HTMLElement;

    readonly deepLTranslateDestinationPoisTarget!: HTMLInputElement;
    readonly deepLTranslateDestinationPoisOptionsTarget!: HTMLElement;
    readonly overwriteExistingDestinationPoiTranslationTarget!: HTMLInputElement;
    readonly overwriteExistingDestinationPoiTranslationOptionsTarget!: HTMLElement;

    readonly deepLTranslateTourAndTourPoisTarget!: HTMLInputElement;
    readonly deepLTranslateTourAndTourPoisOptionsTarget!: HTMLElement;
    readonly translateTourOverviewTarget!: HTMLInputElement;
    readonly translateTourOverviewOptionsTarget!: HTMLElement;

    readonly translateTourPoisTarget!: HTMLInputElement;
    readonly translateTourPoisOptionsTarget!: HTMLElement;
    readonly overwriteExistingTourPoiTranslationTarget!: HTMLInputElement;
    readonly overwriteExistingTourPoiTranslationOptionsTarget!: HTMLElement;

    readonly includePrivateContentTarget!: HTMLInputElement;

    readonly disableSafetyChecksTarget!: HTMLInputElement;

    connect() {
        const form = $(this.formTarget);
        initializeTooltips(form);

        formSubmitWithWaitForJobCompletion(
            form,
            $(this.submitTarget),
            this.modelValue.getJobUrl,
            () => {
                if (this.modelValue.navigateBackAfterTranslationAdded) {
                    history.back();
                } else {
                    const newLanguage = this.translationModeTarget.value == TranslationMode.DeepL.toString()
                        ? this.selectedDeepLTranslationTargetLanguageTarget.value
                        : this.selectedManualTranslationTargetLanguageTarget.value;
                    const translationCompletedNavigateToUrl = this.modelValue.translationCompletedNavigateToUrl.replace("$language", newLanguage);

                    Turbo.visit(translationCompletedNavigateToUrl);
                }
            },
            translate(this.modelValue.dialogConfirmationResourceName)
        );

        selectize($(this.selectedDeepLTranslationSourceLanguageTarget), {
            onChange: () => this.onDeepLTranslationSourceLanguageChanged(),
            score: getScoreFunctionPreservingOrderInSearch
        });
        selectize($(this.selectedDeepLTranslationTargetLanguageTarget), {
            onChange: () => this.onDeepLTranslationNewLanguageChanged(),
            score: getScoreFunctionPreservingOrderInSearch
        });
        selectize($(this.selectedManualTranslationTargetLanguageTarget), {
            onChange: () => this.onManualTranslationNewLanguageChanged(),
            score: getScoreFunctionPreservingOrderInSearch
        });
    }

    onDeepLTranslationSourceLanguageChanged() {
        $(this.selectedDeepLTranslationSourceLanguageTarget).valid();
    }

    onDeepLTranslationNewLanguageChanged() {
        $(this.selectedDeepLTranslationTargetLanguageTarget).valid();
    }

    onManualTranslationNewLanguageChanged() {
        $(this.selectedManualTranslationTargetLanguageTarget).valid();
    }

    readonly selectedTranslationModeClassName = "selected-translation-mode-box";

    deepLTranslationSelected() {
        this.translationModeTarget.value = TranslationMode.DeepL.toString();
        this.translateDeepLBoxTarget.classList.add(this.selectedTranslationModeClassName);
        this.translateManuallyBoxTarget.classList.remove(this.selectedTranslationModeClassName);
    }

    manualTranslationSelected() {
        this.translationModeTarget.value = TranslationMode.Manual.toString();
        this.translateManuallyBoxTarget.classList.add(this.selectedTranslationModeClassName);
        this.translateDeepLBoxTarget.classList.remove(this.selectedTranslationModeClassName);
    }

    translateDestinationOverviewToggled() {
        this.hideOrShowCollapsible(this.translateDestinationOverviewTarget, this.translateDestinationOverviewOptionsTarget);
    }

    private hideOrShowCollapsible(chekbox: HTMLInputElement, collapsible: HTMLElement) {
        if (!chekbox.checked) {
            // @ts-ignore
            $(collapsible).collapse("hide");
            return;
        }

        // @ts-ignore
        $(collapsible).collapse("show");
    }

    deepLTranslateDestinationPoisToggled() {
        this.hideOrShowCollapsible(this.deepLTranslateDestinationPoisTarget, this.deepLTranslateDestinationPoisOptionsTarget);
    }

    overwriteExistingDestinationPoiTranslationToggled() {
        this.hideOrShowCollapsible(this.overwriteExistingDestinationPoiTranslationTarget, this.overwriteExistingDestinationPoiTranslationOptionsTarget);
    }

    deepLTranslateTourAndTourPoisToggled() {
        this.hideOrShowCollapsible(this.deepLTranslateTourAndTourPoisTarget, this.deepLTranslateTourAndTourPoisOptionsTarget);
    }

    translateTourOverviewToggled() {
        this.hideOrShowCollapsible(this.translateTourOverviewTarget, this.translateTourOverviewOptionsTarget);
    }

    translateTourPoisToggled() {
        this.hideOrShowCollapsible(this.translateTourPoisTarget, this.translateTourPoisOptionsTarget);
    }

    overwriteExistingTourPoiTranslationToggled() {
        this.hideOrShowCollapsible(this.overwriteExistingTourPoiTranslationTarget, this.overwriteExistingTourPoiTranslationOptionsTarget);
    }

    disableSafetyChecksToggled() {
        const _this = this;
        
        if (!_this.disableSafetyChecksTarget.checked) return;

        bootbox.confirm({
            message: `Please confirm you would like to disable translation safety checks. Please note that doing so might accidentally break the target language,
                      which might be the main source translation language. The safety checks prevent the translation if:
                      <ul>
                        <li>updating a target language which has not been originally translated</li>
                        <li>the original translation source language is different</li>
                        <li>number of Places in the source tour language is less than the number of tour Places in the target language (tour translation update only)</li>
                      </ul>`,
            buttons: {
                confirm: {
                    label: translate(TranslationsKeys.Global_Ok),
                    className: dialogConfirmationButtonClassName
                },
                cancel: {
                    label: translate(TranslationsKeys.Global_Cancel),
                    className: dialogCancelButtonClassName
                }
            },
            callback(result) {
                if (!result) {
                    _this.disableSafetyChecksTarget.checked = false;
                    return;
                };
            }
        });
    }

    includePrivateContentToggled() {
        const _this = this;
        
        if (!_this.includePrivateContentTarget.checked) return;

        bootbox.confirm({
            message: '<i class="fa-solid fa-triangle-exclamation fa-2xl"></i> Private content will be free in the target language.',
            buttons: {
                confirm: {
                    label: translate(TranslationsKeys.Global_Ok),
                    className: dialogConfirmationButtonClassName
                },
                cancel: {
                    label: translate(TranslationsKeys.Global_Cancel),
                    className: dialogCancelButtonClassName
                }
            },
            callback(result) {
                if (!result) {
                    _this.includePrivateContentTarget.checked = false;
                    return;
                };
            }
        });
    }    
}